/**
 * Aggregates article metadata (seoConfig) with localized bodies (articlesContent.*).
 */
import { ARTICLES, ARTICLE_KEYS, articlePageKey, pagePath } from './seoConfig.mjs';
import { ARTICLES_FR } from './articlesContent.fr';
import { ARTICLES_EN } from './articlesContent.en';

const BODIES = { fr: ARTICLES_FR, en: ARTICLES_EN };

export const ARTICLE_DATE = '2026-10-18';

/** Stock photo (Unsplash, from the design guidelines) used as the hub hero. */
export const TIPS_HERO_IMAGE = 'https://images.unsplash.com/photo-1611619826760-57b6c7e2de8b?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80';

export const AXIS_NAMES = {
  fr: { 1: 'Esth\u00e9tique faciale', 2: 'Structure corporelle', 3: 'Style vestimentaire', 4: 'Grooming', 5: 'Pr\u00e9sence sociale' },
  en: { 1: 'Facial aesthetics', 2: 'Body structure', 3: 'Clothing style', 4: 'Grooming', 5: 'Social presence' },
};

export const TIPS_COPY = {
  fr: {
    eyebrow: 'Conseils par axe',
    h1: 'Des leviers concrets, un axe \u00e0 la fois',
    sub: '{count} articles courts pour appliquer la m\u00e9thode des 5 axes : chacun se lit en quelques minutes et se termine par un plan sur 7 jours.',
    heroAlt: 'D\u00e9tail \u00e9ditorial d\u2019un costume sombre et d\u2019une montre, lumi\u00e8re chaude',
    readingTime: 'min de lecture',
    readMore: 'Lire l\u2019article',
    allTips: 'Tous les conseils',
    byAxis: 'Axe',
    guideLinkTitle: 'La m\u00e9thode compl\u00e8te',
    guideLinkText: 'Les cinq axes expliqu\u00e9s en d\u00e9tail, avec le plan d\u2019action sur 12 semaines.',
    guideLinkCta: 'Lire le guide',
    inGuide: 'Dans le guide',
    inGuideText: 'Cet article d\u00e9taille un levier de l\u2019axe',
    inGuideCta: 'Voir l\u2019axe dans le guide',
    related: 'Articles li\u00e9s',
    toc: 'Sommaire',
    updated: 'Mis \u00e0 jour le',
    author: 'Par l\u2019\u00e9quipe PrimeFace',
    prev: 'Article pr\u00e9c\u00e9dent',
    next: 'Article suivant',
    ctaTitle: 'Passez du conseil au plan personnalis\u00e9',
    ctaText: 'PrimeFace \u00e9value vos cinq axes sur photo et g\u00e9n\u00e8re votre routine hebdomadaire avec rappels. Mode invit\u00e9 disponible.',
    ctaLabel: 'Obtenir mon diagnostic',
    deepen: 'Pour approfondir',
    coverCredit: 'Illustration g\u00e9n\u00e9r\u00e9e par IA',
  },
  en: {
    eyebrow: 'Tips by axis',
    h1: 'Concrete levers, one axis at a time',
    sub: '{count} short articles to apply the 5-axis method: each reads in a few minutes and ends with a 7-day plan.',
    heroAlt: 'Editorial detail of a dark suit and a watch in warm light',
    readingTime: 'min read',
    readMore: 'Read the article',
    allTips: 'All tips',
    byAxis: 'Axis',
    guideLinkTitle: 'The complete method',
    guideLinkText: 'The five axes explained in detail, with the 12-week action plan.',
    guideLinkCta: 'Read the guide',
    inGuide: 'In the guide',
    inGuideText: 'This article details one lever of the axis',
    inGuideCta: 'See the axis in the guide',
    related: 'Related articles',
    toc: 'Contents',
    updated: 'Updated on',
    author: 'By the PrimeFace team',
    prev: 'Previous article',
    next: 'Next article',
    ctaTitle: 'Go from tips to a personalized plan',
    ctaText: 'PrimeFace assesses your five axes from a photo and generates your weekly routine with reminders. Guest mode available.',
    ctaLabel: 'Get my diagnosis',
    deepen: 'Go deeper',
    coverCredit: 'AI-generated illustration',
  },
};

const wordCount = (body) => [body.excerpt, ...body.sections.flatMap(s => [s.title, ...s.blocks.flatMap(b => (b.type === 'ul' ? b.items : [b.text, b.title].filter(Boolean)))])]
  .join(' ').split(/\s+/).filter(Boolean).length;

/** Full article object for a key + language, or null. */
export const getArticle = (key, lang) => {
  const cfg = ARTICLES[key];
  const body = BODIES[lang]?.[key];
  if (!cfg || !body) return null;
  return {
    key,
    pageKey: articlePageKey(key),
    axis: cfg.axis,
    axisName: AXIS_NAMES[lang][cfg.axis],
    path: cfg.paths[lang],
    paths: cfg.paths,
    meta: cfg.meta[lang],
    cover: `/articles/${key}.jpg`,
    minutes: Math.max(1, Math.round(wordCount(body) / 200)),
    ...body,
  };
};

/** All articles for a language, ordered by axis then key order. */
export const listArticles = (lang) => ARTICLE_KEYS.map(k => getArticle(k, lang)).filter(Boolean).sort((a, b) => a.axis - b.axis);

/** Hub subtitle with the live article count (FR/EN number words up to 20, digits beyond). */
const COUNT_WORDS = {
  fr: ['z\u00e9ro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf', 'vingt'],
  en: ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'],
};
export const tipsSubtitle = (lang) => {
  const n = listArticles(lang).length;
  const word = COUNT_WORDS[lang]?.[n] || String(n);
  const label = word.charAt(0).toUpperCase() + word.slice(1);
  return TIPS_COPY[lang].sub.replace('{count}', label);
};

/** Articles attached to a guide axis section id (fr: axe-1, en: axis-1). */
export const articlesForGuideAnchor = (anchor, lang) => listArticles(lang).filter(a => a.guideAnchor === anchor);

export const tipsPath = (lang) => pagePath('tips', lang);
